'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useInsertionEffect,
  useRef,
} from 'react';

/**
 * Lightweight settlement tracking for tool UI rendering.
 *
 * After a thread switch, tool UIs (carousels, cards) render asynchronously and
 * cause layout shifts that can leave the viewport not scrolled to the bottom.
 * The library's built-in scroll mechanisms can miss these late resizes.
 *
 * This system provides a signal-based solution:
 * 1. Each tool group registers a token on mount via `useSettlementToken`
 * 2. Tokens resolve when the tool UI finishes rendering (after paint)
 * 3. When all tokens resolve, `onSettle` fires → thread scrolls to bottom
 *
 * Pass `resetKey` (e.g. the current threadId) to reset pending tokens on thread
 * switch without unmounting the subtree — imperative reset avoids the full
 * carousel/Swiper tear-down that a `key` change would cause.
 */

type RegisterFn = () => () => void;

const ContentSettlementContext = createContext<RegisterFn | null>(null);

interface ContentSettlementProviderProps {
  onSettle: () => void;
  /** Change to imperatively reset pending tokens (e.g. threadId). */
  resetKey?: string | number;
  children: ReactNode;
}

export function ContentSettlementProvider({
  onSettle,
  resetKey,
  children,
}: ContentSettlementProviderProps) {
  const pendingRef = useRef<Set<symbol>>(new Set());
  const onSettleRef = useRef(onSettle);
  useInsertionEffect(() => {
    onSettleRef.current = onSettle;
  });
  const rafIdRef = useRef(0);

  const scheduleSettle = useCallback(() => {
    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => {
      if (pendingRef.current.size === 0) {
        onSettleRef.current();
      }
    });
  }, []);

  const register = useCallback((): (() => void) => {
    const token = Symbol();
    pendingRef.current.add(token);

    return () => {
      if (!pendingRef.current.delete(token)) return;
      if (pendingRef.current.size === 0) {
        // Defer to let any new registrations from the same render cycle happen first
        scheduleSettle();
      }
    };
  }, [scheduleSettle]);

  // Runs on mount and whenever resetKey changes. Schedules a settle check so
  // text-only threads still fire onSettle. Passive effect (not layout) so
  // children's `useSettlementToken` effects run first and register their tokens
  // before this rAF fires — otherwise we'd settle prematurely during a thread
  // switch, before MessageLoadingToken registers its pending token. We
  // intentionally do NOT clear pendingRef here for the same reason.
  useEffect(() => {
    scheduleSettle();
    return () => cancelAnimationFrame(rafIdRef.current);
  }, [resetKey, scheduleSettle]);

  return (
    <ContentSettlementContext.Provider value={register}>
      {children}
    </ContentSettlementContext.Provider>
  );
}

/**
 * Registers a settlement token on mount.
 *
 * - While `isLoading` is true: stays registered (resolves when loading finishes)
 * - When `isLoading` is false: resolves after a double rAF (content has been painted)
 * - On unmount: resolves immediately (cleanup)
 *
 * The double rAF ensures the browser has painted the content and layout shifts
 * from the initial render have completed before signaling settlement.
 */
export function useSettlementToken(isLoading: boolean): void {
  const register = useContext(ContentSettlementContext);

  useEffect(() => {
    if (!register) return;
    const resolve = register();

    if (!isLoading) {
      // Content is ready — resolve after paint so layout shifts have settled
      let outerRaf = requestAnimationFrame(() => {
        outerRaf = requestAnimationFrame(() => resolve());
      });
      return () => {
        cancelAnimationFrame(outerRaf);
        resolve();
      };
    }

    // Still loading — resolve on cleanup (when isLoading becomes false, effect re-runs)
    return resolve;
  }, [register, isLoading]);
}

/**
 * Holds settlement while mounted. As a Suspense fallback, it keeps the thread from settling (and
 * scrolling to the bottom) before the suspended content and its own tokens have mounted.
 */
export function SettlementHold(): null {
  useSettlementToken(true);
  return null;
}
